import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { FormattedMessage as T } from "react-intl";
import {
  DuplicateHandlingMode,
  SkuQualificationPreviewResponse,
} from "services/KnowfullyAdminClient";
import { QualificationIds } from "constants/QualificationIds";

const styles = {
  content: {
    marginBottom: 2,
  },
  existingList: {
    marginBottom: 2,
    paddingLeft: 2,
  },
  existingItem: {
    marginBottom: 1,
    fontWeight: "bold",
  },
  existingItemDates: {
    fontSize: "0.875rem",
    fontWeight: "normal",
    color: "text.secondary",
    marginLeft: 1,
  },
  warning: {
    marginTop: 2,
  },
};

const formatDateRange = (
  releaseDate: Date | undefined,
  expirationDate: Date | undefined,
) => {
  const start = releaseDate ? new Date(releaseDate).toLocaleDateString() : null;
  const end = expirationDate
    ? new Date(expirationDate).toLocaleDateString()
    : null;

  if (start && end) return `${start} - ${end}`;
  if (start) return `From ${start}`;
  if (end) return `Until ${end}`;
  return null;
};

type BulkSkuQualificationConfirmDialogProps = {
  open: boolean;
  loading: boolean;
  qualificationId?: string | null;
  useQualificationApprovals: boolean;
  previewResponse: SkuQualificationPreviewResponse | null;
  onConfirm: (duplicateHandling?: DuplicateHandlingMode) => void;
  onCancel: () => void;
  error?: string | null;
};

const BulkSkuQualificationConfirmDialog = ({
  open,
  loading,
  qualificationId,
  useQualificationApprovals,
  previewResponse,
  onConfirm,
  onCancel,
  error,
}: BulkSkuQualificationConfirmDialogProps) => {
  const hasExistingQualifications = (previewResponse?.totalExisting ?? 0) > 0;
  const hasNewQualifications = (previewResponse?.totalNew ?? 0) > 0;
  const isAcpeQualification = qualificationId === QualificationIds.Acpe;

  return (
    <Dialog open={open} disableEnforceFocus onClose={onCancel}>
      <DialogTitle>
        <T id="areYouSure" />
      </DialogTitle>
      <DialogContent dividers>
        {useQualificationApprovals && (
          <Box sx={styles.content}>
            <T id="bulkAddSkuQualificationConfirmationText" />
          </Box>
        )}

        {hasNewQualifications && (
          <Box sx={styles.content}>
            <T
              id="bulkSkuAccreditationBodyNewCountText"
              values={{ count: previewResponse?.totalNew }}
            />
          </Box>
        )}

        {hasExistingQualifications && (
          <>
            <Box sx={styles.content}>
              <T id="bulkSkuAccreditationBodyExistingWarningText" />
            </Box>
            <Box sx={styles.existingList}>
              {previewResponse?.existingQualifications?.map((item, index) => (
                <Box
                  key={`${item.qualificationName}-${item.professionName}-${item.stateName}-${index}`}
                  sx={styles.existingItem}
                >
                  <T
                    id="bulkSkuQualificationExistingItemFormat"
                    values={{
                      qualification: item.qualificationName,
                      profession: item.professionName || "Any Profession",
                      state: item.stateName || "Any State",
                    }}
                  />
                  {formatDateRange(
                    item.releaseDate,
                    item.creditSubmissionExpirationDate,
                  ) && (
                    <Box component="span" sx={styles.existingItemDates}>
                      (
                      {formatDateRange(
                        item.releaseDate,
                        item.creditSubmissionExpirationDate,
                      )}
                      )
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
            <Box>
              <T id="bulkSkuQualificationOverridePromptText" />
            </Box>
          </>
        )}

        {!useQualificationApprovals && !hasExistingQualifications && (
          <T id="bulkSkuQualificationGenericConfirmationText" />
        )}

        {isAcpeQualification && (
          <Alert sx={styles.warning} severity="warning">
            <T id="acpeBulkAddWarning" />
          </Alert>
        )}

        {error && (
          <Alert sx={styles.warning} severity="error">
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {hasExistingQualifications ? (
          <>
            <Button onClick={onCancel} disabled={loading}>
              <T id="returnButton" />
            </Button>
            <Button
              onClick={() => onConfirm(DuplicateHandlingMode.SkipExisting)}
              color="primary"
              disabled={loading}
            >
              <T id="createNewSkipExistingButton" />
            </Button>
            <Button
              onClick={() => onConfirm(DuplicateHandlingMode.UpdateExisting)}
              color="primary"
              disabled={loading}
            >
              <T id="overrideButton" />
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onCancel} disabled={loading}>
              <T id="cancelButton" />
            </Button>
            <Button
              onClick={() => onConfirm()}
              color="primary"
              disabled={loading}
            >
              <T id="okButton" />
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkSkuQualificationConfirmDialog;
